"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { usePathname } from "next/navigation";
import { getSiteContent, siteContent, SUPPORTED_LOCALES, type LocaleKey, type SiteContent } from "../content";

type LanguageContextValue = {
  locale: LocaleKey;
  t: SiteContent;
  setLocale: (locale: LocaleKey) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

type LanguageProviderProps = {
  children: ReactNode;
  initialLocale?: LocaleKey;
};

export function LanguageProvider({ children, initialLocale = "en" }: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<LocaleKey>(initialLocale);
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) {
      return;
    }
    const pathSegment = pathname.split("/").filter(Boolean)[0];
    const nextLocale = SUPPORTED_LOCALES.includes(pathSegment as LocaleKey)
      ? (pathSegment as LocaleKey)
      : initialLocale;

    setLocaleState((prev) => (prev === nextLocale ? prev : nextLocale));
  }, [pathname, initialLocale]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      const html = document.documentElement;
      html.setAttribute("lang", siteContent[locale].common.lang);
      html.setAttribute("dir", siteContent[locale].common.dir);
    }
  }, [locale]);

  const setLocale = useCallback((next: LocaleKey) => {
    setLocaleState(next);
  }, []);

  const translation = useMemo(() => getSiteContent(locale), [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      t: translation,
      setLocale
    }),
    [locale, translation, setLocale]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("LanguageContext is not available");
  }
  return ctx;
}
